import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn,
  faYoutube,
  faInstagram
} from '@fortawesome/free-brands-svg-icons';
import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faChevronRight,
  faShieldAlt,
  faHeart
} from '@fortawesome/free-solid-svg-icons';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <>
      <style>{`
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0, -8px, 0);
          }
          70% {
            transform: translate3d(0, -4px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }
        
        .animation-delay-100 {
          animation-delay: 100ms;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        
        .group-hover\\:bounce:hover {
          animation: bounce 0.6s ease-in-out;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .group-hover\\:float:hover {
          animation: float 2s ease-in-out infinite;
        }
        
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        .group-hover\\:shimmer:hover {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out;
        }
      `}</style>
      
      <footer className="relative bg-gradient-to-br from-white via-gray-50 to-blue-50 text-gray-800 overflow-hidden border-t border-gray-200">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-blue-100/30 to-purple-100/30 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo and Social Icons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-center lg:justify-start">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-lg border border-gray-200">
                <img 
                  src="/images/iinsaf.png"
                  alt="iinsaf Logo"
                  className="h-12 w-auto"
                />
              </div>
            </div>
            <p className="text-gray-600 leading-relaxed text-center lg:text-left">
              <span className="text-red-500 font-bold">ii</span>
              <span className="font-bold text-gray-800">nsaf</span> is a leading platform dedicated to promoting social justice
              through innovative technological solutions and digital empowerment.
            </p>
            <div className="flex justify-center lg:justify-start space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.15, 
                    y: -5,
                    rotate: [0, -5, 5, 0],
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ scale: 0.9 }}
                  className="group relative"
                  aria-label={social.name}
                >
                  <div className="w-10 h-10 rounded-xl bg-white/80 backdrop-blur-sm flex items-center justify-center group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-purple-500 transition-all duration-300 shadow-md border border-gray-200 group-hover:shadow-xl group-hover:border-transparent group-hover:rotate-12">
                    <FontAwesomeIcon icon={social.icon} className="text-sm text-gray-600 group-hover:text-white group-hover:scale-110 transition-all duration-300" />
                  </div>
                  {/* Floating particles effect */}
                  <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute -top-1 -left-1 w-2 h-2 bg-blue-400 rounded-full animate-ping"></div>
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping animation-delay-100"></div>
                    <div className="absolute -bottom-1 -left-1 w-1 h-1 bg-pink-400 rounded-full animate-ping animation-delay-200"></div>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 className="text-xl font-bold text-gray-800 text-center lg:text-left">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className="group flex items-center justify-center lg:justify-start text-gray-600 hover:text-gray-800 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="w-6 h-6 rounded-lg bg-white/80 backdrop-blur-sm flex items-center justify-center mr-3 group-hover:bg-blue-500 group-hover:scale-110 transition-all duration-300 shadow-sm border border-gray-200 group-hover:border-transparent group-hover:rotate-6 group-hover:shadow-lg">
                      <FontAwesomeIcon icon={faChevronRight} className="text-xs text-gray-500 group-hover:text-white group-hover:translate-x-0.5 group-hover:scale-125 transition-all duration-300" />
                    </div>
                    <span className="group-hover:translate-x-1 transition-transform duration-300 group-hover:font-semibold">{link.name}</span>
                    {/* Animated underline */}
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-300 ease-out"></div>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-20 group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-opacity duration-300 blur-sm"></div>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Privacy Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-6"
          >
            <h3 className="text-xl font-bold text-gray-800 text-center lg:text-left">
              Privacy & Legal
            </h3>
            <ul className="space-y-3">
              {privacyLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className="group flex items-center justify-center lg:justify-start text-gray-600 hover:text-gray-800 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="w-6 h-6 rounded-lg bg-white/80 backdrop-blur-sm flex items-center justify-center mr-3 group-hover:bg-green-500 group-hover:scale-110 transition-all duration-300 shadow-sm border border-gray-200 group-hover:border-transparent group-hover:rotate-6 group-hover:shadow-lg">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-xs text-gray-500 group-hover:text-white group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                    </div>
                    <span className="group-hover:translate-x-1 transition-transform duration-300 group-hover:font-semibold">{link.name}</span>
                    {/* Animated underline */}
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300 ease-out"></div>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-20 group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-emerald-400 transition-opacity duration-300 blur-sm"></div>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            <h3 className="text-xl font-bold text-gray-800 text-center lg:text-left">
              Get In Touch
            </h3>
            <ul className="space-y-4">
              {contactInfo.map((info, index) => (
                <motion.li
                  key={info.type}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                >
                  <a
                    href={info.href}
                    className="group flex items-center justify-center lg:justify-start text-gray-600 hover:text-gray-800 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/80 backdrop-blur-sm flex items-center justify-center mr-4 group-hover:bg-gradient-to-r group-hover:from-orange-500 group-hover:to-red-500 group-hover:scale-110 transition-all duration-300 shadow-md border border-gray-200 group-hover:border-transparent group-hover:rotate-6 group-hover:shadow-xl">
                      <FontAwesomeIcon icon={info.icon} className="text-sm text-gray-500 group-hover:text-white group-hover:scale-125 group-hover:bounce transition-all duration-300" />
                    </div>
                    <span className="group-hover:translate-x-1 transition-transform duration-300 group-hover:font-semibold">{info.value}</span>
                    {/* Animated underline */}
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300 ease-out"></div>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-20 group-hover:bg-gradient-to-r group-hover:from-orange-400 group-hover:to-red-400 transition-opacity duration-300 blur-sm"></div>
                    {/* Pulse effect */}
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 rounded-xl border-2 border-orange-400 animate-ping"></div>
                    </div>
                  </a>
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <div className="flex items-center justify-center lg:justify-start text-gray-600 group hover:text-gray-800 transition-all duration-300 relative overflow-hidden">
                  <div className="w-10 h-10 rounded-xl bg-white/80 backdrop-blur-sm flex items-center justify-center mr-4 shadow-md border border-gray-200 group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-indigo-500 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-xl group-hover:border-transparent transition-all duration-300">
                    <FontAwesomeIcon icon={faLocationDot} className="text-sm text-gray-500 group-hover:text-white group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                  </div>
                  <address className="not-italic leading-relaxed group-hover:translate-x-1 group-hover:font-semibold transition-all duration-300">
                    419, Sunder Nagar,<br />
                    Hisar, Haryana - 125001
                  </address>
                  {/* Animated underline */}
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 group-hover:w-full transition-all duration-300 ease-out"></div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-20 group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-indigo-400 transition-opacity duration-300 blur-sm"></div>
                </div>
              </motion.li>
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: isVisible ? 1 : 0, scaleX: isVisible ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="my-12"
        >
          <hr className="border-gray-300" />
        </motion.div>

        {/* Copyright Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-center space-y-4"
        >
          <p className="text-gray-600 flex items-center justify-center gap-2">
            Made with <FontAwesomeIcon icon={faHeart} className="text-red-500 animate-pulse" /> by{" "}
            <a
              href="https://www.iinsaf.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-800 font-semibold hover:text-blue-600 transition-colors duration-300"
            >
              IINSAF Team
            </a>
          </p>
          <p className="text-sm text-gray-500">
            Copyright © {new Date().getFullYear()} IINSAF. All rights reserved.
          </p>
          <div className="flex justify-center items-center space-x-6 text-sm text-gray-500">
            <Link to="/termscondition" className="hover:text-gray-700 transition-colors duration-300 hover:underline relative group">
              <span className="relative z-10">Terms & Conditions</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-300 ease-out"></div>
            </Link>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <Link to="/privacypolicy" className="hover:text-gray-700 transition-colors duration-300 hover:underline relative group">
              <span className="relative z-10">Privacy Policy</span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300 ease-out"></div>
            </Link>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <Link to="/refundpolicy" className="hover:text-gray-700 transition-colors duration-300 hover:underline relative group">
              <span className="relative z-10">Refund Policy</span>
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300 ease-out"></div>
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
    </>
  );
};

// Data arrays for footer content
const socialLinks = [
  { name: 'Facebook', icon: faFacebookF, href: 'https://www.facebook.com/iinsafofficial' },
  { name: 'Twitter', icon: faTwitter, href: 'https://x.com/iinsafofficial' },
  { name: 'LinkedIn', icon: faLinkedinIn, href: 'https://linkedin.com/company/iinsaaf' },
  { name: 'YouTube', icon: faYoutube, href: 'https://youtube.com/@iinsafofficial?si=PeyXAbIphT1Gqiac' },
  { name: 'Instagram', icon: faInstagram, href: 'https://instagram.com/iinsaaf' },
];

const quickLinks = [
  { name: 'Homepage', href: '/' },
  { name: 'About Us', href: '/about' },
  { name: 'FAQs', href: '/faqs' },
  { name: 'Join as Reporter', href: '/register' },
  { name: 'Join as Advertiser', href: '/register' },
];

const privacyLinks = [
  { name: 'Terms and Conditions', href: '/termscondition' },
  { name: 'Privacy Policy', href: '/privacypolicy' },
  { name: 'Refund Policy', href: '/refundpolicy' },
  { name: 'Disclaimer', href: '/disclaimer' },
  { name: 'User Guidelines', href: '/guidelines' },
];

const contactInfo = [
  { type: 'phone', icon: faPhone, value: '+91 99923-96623', href: 'tel:+919992396623' },
  { type: 'email', icon: faEnvelope, value: 'iinsafgroup@gamil.com', href: 'mailto:iinsafgroup@gamil.com' },
];

export default Footer;